import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronRight, ChevronLeft, HelpCircle, Sparkles } from 'lucide-react';

const TOUR_VERSION = 'v2';
const TOOLTIP_W = 340;
const GAP = 14;
const PADDING = 16;

function getRect(selector) {
  if (!selector) return null;
  const el = document.querySelector(selector);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  return { x: r.left, y: r.top, w: r.width, h: r.height };
}

function tooltipPosition(rect, placement) {
  if (!rect) return null;
  const vpW = window.innerWidth;
  const vpH = window.innerHeight;
  const APPROX_H = 190;
  let x, y, arrowSide;

  const fitsBelow = rect.y + rect.h + GAP + APPROX_H < vpH - PADDING;
  const fitsAbove = rect.y - GAP - APPROX_H > PADDING;

  if (placement === 'top' || (!fitsBelow && fitsAbove)) {
    x = rect.x + rect.w / 2 - TOOLTIP_W / 2;
    y = rect.y - APPROX_H - GAP;
    arrowSide = 'bottom';
  } else {
    x = rect.x + rect.w / 2 - TOOLTIP_W / 2;
    y = rect.y + rect.h + GAP;
    arrowSide = 'top';
  }

  x = Math.max(PADDING, Math.min(x, vpW - TOOLTIP_W - PADDING));
  y = Math.max(PADDING, y);
  return { x, y, arrowSide };
}

// ─── MAIN COMPONENT ──────────────────────────────────────────────────────────
export function TourGuide({ tourId, steps, enabled = true }) {
  const storageKey = `tour_done_${tourId}_${TOUR_VERSION}`;
  const [active, setActive] = useState(false);
  const [step, setStep] = useState(0);
  const [rect, setRect] = useState(null);
  const rafRef = useRef(null);

  // Auto-start on first visit
  useEffect(() => {
    if (!enabled) return;
    if (!localStorage.getItem(storageKey)) {
      const t = setTimeout(() => { setActive(true); setStep(0); }, 1400);
      return () => clearTimeout(t);
    }
  }, [storageKey, enabled]);

  // Update rect for current step
  const updateRect = useCallback(() => {
    const s = steps[step];
    if (!s || !active) return;
    if (s.target) {
      const r = getRect(s.target);
      setRect(r);
      if (!r) {
        // If target not found, scroll and retry once
        setTimeout(() => setRect(getRect(s.target)), 400);
      }
    } else {
      setRect(null);
    }
  }, [step, active, steps]);

  useEffect(() => {
    if (!active) return;
    updateRect();
    window.addEventListener('resize', updateRect);
    window.addEventListener('scroll', updateRect, true);
    return () => {
      window.removeEventListener('resize', updateRect);
      window.removeEventListener('scroll', updateRect, true);
    };
  }, [active, updateRect]);

  // Scroll target into view
  useEffect(() => {
    if (!active) return;
    const s = steps[step];
    if (s?.target) {
      const el = document.querySelector(s.target);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [active, step, steps]);

  const finish = useCallback(() => {
    localStorage.setItem(storageKey, '1');
    setActive(false);
  }, [storageKey]);

  const goNext = () => { if (step < steps.length - 1) setStep(s => s + 1); else finish(); };
  const goPrev = () => { if (step > 0) setStep(s => s - 1); };

  const current = steps[step];
  const isLast = step === steps.length - 1;
  const pos = rect ? tooltipPosition(rect, current?.placement) : null;

  // Progress dots — expand active dot
  const Dots = () => (
    <div className="flex items-center gap-1">
      {steps.map((_, i) => (
        <motion.div
          key={`dot-${i}`}
          animate={{ width: i === step ? 16 : 6 }}
          className={`h-1.5 rounded-full transition-colors ${
            i === step ? 'bg-emerald-500' : i < step ? 'bg-emerald-300' : 'bg-slate-200 dark:bg-slate-600'
          }`}
          style={{ minWidth: 6 }}
        />
      ))}
    </div>
  );

  // Tooltip card
  const Tooltip = () => (
    <motion.div
      key={`step-${step}`}
      initial={{ opacity: 0, y: rect ? (pos?.arrowSide === 'top' ? -10 : 10) : 0, scale: 0.94 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, scale: 0.94 }}
      transition={{ type: 'spring', stiffness: 380, damping: 28 }}
      onClick={e => e.stopPropagation()}
      style={{
        position: 'fixed',
        zIndex: 99999,
        width: TOOLTIP_W,
        ...(pos
          ? { left: pos.x, top: pos.y }
          : { left: '50%', top: '50%', transform: 'translate(-50%,-50%)' }),
      }}
    >
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl border border-slate-100 dark:border-slate-700 overflow-hidden">
        {/* Brand accent bar */}
        <div className="h-1.5 bg-gradient-to-r from-emerald-500 via-cyan-500 to-blue-500" />

        <div className="p-5 pb-3">
          {/* Header row */}
          <div className="flex items-start justify-between mb-3">
            <Dots />
            <button
              onClick={finish}
              data-testid="tour-skip"
              className="h-6 w-6 rounded-full hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center justify-center text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors flex-shrink-0 ml-2"
              title="Saltar tour"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>

          {/* Icon for center/welcome steps */}
          {!rect && current?.icon && (
            <div className="mb-3 h-10 w-10 rounded-2xl bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
              <current.icon className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
            </div>
          )}

          <h3 className="font-bold text-slate-800 dark:text-white text-base mb-1.5 font-heading leading-snug">
            {current?.title}
          </h3>
          <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed">
            {current?.description}
          </p>
        </div>

        {/* Footer */}
        <div className="px-5 pb-4 pt-1 flex items-center justify-between">
          <span className="text-xs text-slate-400">{step + 1} / {steps.length}</span>
          <div className="flex items-center gap-2">
            {step > 0 && (
              <button
                onClick={goPrev}
                data-testid="tour-prev"
                className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 px-3 py-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700/50 transition-colors"
              >
                <ChevronLeft className="h-3.5 w-3.5" />Atrás
              </button>
            )}
            <button
              onClick={goNext}
              data-testid="tour-next"
              className="flex items-center gap-1.5 text-xs font-semibold text-white bg-gradient-to-r from-emerald-500 to-cyan-600 hover:from-emerald-600 hover:to-cyan-700 px-4 py-1.5 rounded-lg transition-all shadow-sm shadow-emerald-200 dark:shadow-emerald-900/30"
            >
              {isLast ? 'Empezar' : <><span>Siguiente</span><ChevronRight className="h-3.5 w-3.5" /></>}
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );

  return (
    <>
      {/* Floating help button (always visible when tour is not active) */}
      {!active && (
        <motion.button
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 2, type: 'spring' }}
          onClick={() => { setStep(0); setActive(true); }}
          data-testid="tour-help-btn"
          className="fixed bottom-6 right-6 z-50 h-11 w-11 rounded-full bg-emerald-500 hover:bg-emerald-600 active:scale-95 shadow-lg shadow-emerald-300 dark:shadow-emerald-900/40 flex items-center justify-center text-white transition-all hover:scale-105"
          title="Tour guiado"
        >
          <HelpCircle className="h-5 w-5" />
        </motion.button>
      )}

      {/* Tour overlay */}
      {active && createPortal(
        <AnimatePresence>
          <motion.div
            key="tour-root"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{ position: 'fixed', inset: 0, zIndex: 99990 }}
          >
            {/* SVG overlay with spotlight cutout */}
            <svg
              style={{ position: 'fixed', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }}
              xmlns="http://www.w3.org/2000/svg"
            >
              <defs>
                <mask id={`tour-mask-${tourId}`}>
                  <rect fill="white" x="0" y="0" width="100%" height="100%" />
                  {rect && (
                    <rect
                      fill="black"
                      x={rect.x - 8} y={rect.y - 8}
                      width={rect.w + 16} height={rect.h + 16}
                      rx="10"
                    />
                  )}
                </mask>
              </defs>
              <rect
                fill="rgba(2,8,23,0.72)"
                x="0" y="0" width="100%" height="100%"
                mask={`url(#tour-mask-${tourId})`}
              />
            </svg>

            {/* Click overlay (click to advance) */}
            <div
              style={{ position: 'fixed', inset: 0, zIndex: 99991, cursor: 'pointer' }}
              onClick={goNext}
            />

            {/* Spotlight border glow */}
            <AnimatePresence>
              {rect && (
                <motion.div
                  key={`glow-${step}`}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  style={{
                    position: 'fixed',
                    left: rect.x - 8, top: rect.y - 8,
                    width: rect.w + 16, height: rect.h + 16,
                    borderRadius: 10,
                    border: '2px solid rgba(52, 211, 153, 0.8)',
                    boxShadow: '0 0 0 4px rgba(52, 211, 153, 0.15), 0 0 24px rgba(52, 211, 153, 0.25)',
                    zIndex: 99992,
                    pointerEvents: 'none',
                  }}
                />
              )}
            </AnimatePresence>

            {/* Tooltip */}
            <AnimatePresence mode="wait">
              <Tooltip key={`tooltip-${step}`} />
            </AnimatePresence>
          </motion.div>
        </AnimatePresence>,
        document.body
      )}
    </>
  );
}
